import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LoadingStatus } from "../sessions/models/LoadingStatus";
import { ApiErrorResponse } from "../../api/types/ApiErrorResponse";
import { RootState } from "../../app/store";
import { ClosedDay } from "./models/ClosedDay";
import { MarkDayClosedResponse } from "./contracts/MarkDayClosedResponse";
import {
  fetchClosedDays,
  markDayClosed,
  reopenDay,
} from "./slice-extensions/closed-days-slice-extension";

interface ClosedDaysState {
  items: ClosedDay[];
  status: LoadingStatus;
  lastError?: ApiErrorResponse;
}

const initialState: ClosedDaysState = {
  items: [],
  status: LoadingStatus.Idle,
  lastError: undefined,
};

const closedDaysSlice = createSlice({
  name: "closedDays",
  initialState,
  reducers: {
    clearClosedDaysError: (state) => {
      state.lastError = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClosedDays.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.lastError = undefined;
      })
      .addCase(fetchClosedDays.fulfilled, (state, action: PayloadAction<ClosedDay[]>) => {
        state.status = LoadingStatus.Idle;
        state.items = action.payload;
      })
      .addCase(fetchClosedDays.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        state.lastError = action.payload;
      });

    builder
      .addCase(markDayClosed.fulfilled, (state, action: PayloadAction<MarkDayClosedResponse>) => {
        // ClosedDayResponse and the ClosedDay model are the same shape on the wire.
        state.items.push(...action.payload.closedDays.map((day) => ({ ...day })));
      })
      .addCase(markDayClosed.rejected, (state, action) => {
        state.lastError = action.payload;
      });

    builder
      .addCase(reopenDay.fulfilled, (state, action: PayloadAction<string>) => {
        state.items = state.items.filter((day) => day.id !== action.payload);
      })
      .addCase(reopenDay.rejected, (state, action) => {
        state.lastError = action.payload;
      });
  },
});

export const { clearClosedDaysError } = closedDaysSlice.actions;

export const selectClosedDays = (state: RootState) => state.closedDays.items;
export const selectClosedDaysStatus = (state: RootState) => state.closedDays.status;

export default closedDaysSlice.reducer;
